import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { products, addToCart } from '../mock/mockData';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const handleAddToCart = () => {
    setAdding(true);
    addToCart(product);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success(`${product.name} added to cart!`);
    setTimeout(() => setAdding(false), 1000);
  };

  return (
    <Card className="hover:shadow-2xl transition-all duration-300 group">
      <div className="relative overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {product.bestseller && (
          <Badge className="absolute top-4 right-4 bg-red-600 text-white">
            Bestseller
          </Badge>
        )}
        {product.inStock ? (
          <Badge className="absolute top-4 left-4 bg-green-600 text-white">
            In Stock
          </Badge>
        ) : (
          <Badge className="absolute top-4 left-4 bg-slate-400 text-white">
            Out of Stock
          </Badge>
        )}
        {product.material && (
          <Badge className={`absolute bottom-4 right-4 ${
            product.material === 'Nylon' ? 'bg-blue-600' : 'bg-green-600'
          } text-white`}>
            {product.material}
          </Badge>
        )}
        {product.series && (
          <Badge className="absolute bottom-4 left-4 bg-slate-900 text-white">
            {product.series}
          </Badge>
        )}
      </div>
      <CardContent className="p-6">
        <h3 className="font-bold text-xl mb-2 text-slate-900">{product.name}</h3>
        <p className="text-slate-600 text-sm mb-4 min-h-[40px]">{product.description}</p>
        
        {/* Features */}
        <div className="mb-4 space-y-1">
          {product.features.slice(0, 3).map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
              <Check className="w-4 h-4 text-green-600" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <span className="text-3xl font-bold text-red-600">${product.price.toFixed(2)}</span>
          <Button
            onClick={handleAddToCart}
            disabled={!product.inStock || adding}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50"
          >
            {adding ? (
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" /> Added
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" /> Add to Cart
              </span>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const USFlags = () => {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Patriotic background */}
      <div 
        className="fixed inset-0 opacity-5 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1563804846955-f87a66f0b20a?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      ></div>
      <div className="fixed inset-0 bg-gradient-to-b from-slate-50/95 via-red-50/30 to-blue-50/30 z-0"></div>
      
      <div className="relative z-10">
      {/* Hero Section */}
      <section className="bg-white py-12 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold text-slate-900 mb-4">American Flags</h1>
            <p className="text-xl text-slate-600">
              Premium quality U.S. flags made with pride in America. Choose from various sizes and materials for residential or commercial use.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* Material Filter Info */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Endura-Nylon Flags</h3>
                <p className="text-slate-700 text-sm mb-2">
                  Crafted using aniline-dyed 100% heavyweight 200-denier nylon bunting. Fast drying, treated to resist fading, and flies in the slightest breeze.
                </p>
                <p className="text-sm text-slate-600">Best for: Residential use, light to moderate wind conditions</p>
              </CardContent>
            </Card>
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-green-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Poly-Max Flags</h3>
                <p className="text-slate-700 text-sm mb-2">
                  2-ply 100% polyester bunting with open weave construction. Reduces wind resistance, increasing flag life. Extremely durable.
                </p>
                <p className="text-sm text-slate-600">Best for: Commercial applications, high wind areas</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.usFlags.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Why Choose Section */}
          <div className="mt-16 bg-white/90 backdrop-blur-sm rounded-lg p-8 shadow-lg border-2 border-blue-100">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Our American Flags?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-slate-900">Made in USA</h3>
                  <p className="text-slate-600">All our flags are proudly manufactured in the United States using premium materials.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-slate-900">Premium Quality</h3>
                  <p className="text-slate-600">Embroidered stars, sewn stripes, and reinforced stitching for long-lasting durability.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-slate-900">Weather Resistant</h3>
                  <p className="text-slate-600">UV-resistant materials designed to withstand harsh outdoor conditions.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-slate-900">Fast Shipping</h3>
                  <p className="text-slate-600">Quick processing and shipping to get your flag flying as soon as possible.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
};

export default USFlags;